import React, { useState, useMemo, useEffect } from 'react';
import { Loader2, Send, Info, Activity, Receipt, Camera, Plus, X, Clock, CheckCircle, AlertTriangle, RotateCcw } from 'lucide-react';
import { Incarico } from '../../types';
import { RelazioneGiornalieraService } from '../../services/apiService';
import { useToast } from '../ToastContext';
import { parseDecimal } from '../../utils/utils';
import NumberInput from '../NumberInput';
import AutoAziendaleSelector from '../AutoAziendaleSelector';
import { safeAction } from '../../utils/safeAction';

interface Props {
    activeIncarico: Incarico;
    isStoricoActive: boolean;
    selectedDate: string;
    setSelectedDate: (d: string) => void;
    attivitaGps: { ore: number; ids: number[] };
    mediaGiorno: { tuttiIds: number[]; noteIds: number[] };
    reportGiornoCorrente: any;
    onReportSent: () => void;
}

interface SpesaRow {
    dataAttivita: string;
    tipo: string;
    importo: string;
    note: string;
    anticipatoAzienda: boolean;
    file: File | null;
}

export const ReportTab: React.FC<Props> = ({
                                               activeIncarico, isStoricoActive, selectedDate, setSelectedDate,
                                               attivitaGps, mediaGiorno, reportGiornoCorrente, onReportSent,
                                           }) => {
    const { addToast } = useToast();
    const [relazioneTesto, setRelazioneTesto] = useState('');
    const [kmFatti, setKmFatti] = useState('');
    const [autoAziendale, setAutoAziendale] = useState(false);
    const [oreExtra, setOreExtra] = useState('');
    const [orarioInizioIndagine, setOrarioInizioIndagine] = useState('');
    const [orarioFineIndagine, setOrarioFineIndagine] = useState('');
    const [listaSpese, setListaSpese] = useState<SpesaRow[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    // Rettifica spontanea del collaboratore: riapre il form come per DA_MODIFICARE
    // e usa lo stesso endpoint /modifica, che rimanda la relazione in revisione.
    const [userInitiatedEdit, setUserInitiatedEdit] = useState(false);

    // Form di modifica: report rimandato indietro dal titolare (DA_MODIFICARE)
    // oppure rettifica avviata dal collaboratore. Le spese non si reinviano.
    const isEditingExisting = reportGiornoCorrente?.stato === 'DA_MODIFICARE' || userInitiatedEdit;

    // "Modifica relazione" solo per APPROVATO e DA_REVISIONARE: RIFIUTATO lo
    // gestisce il titolare e DA_MODIFICARE ha già il suo banner.
    const canCollaboratorEdit = !!reportGiornoCorrente
        && (reportGiornoCorrente.stato === 'APPROVATO' || reportGiornoCorrente.stato === 'DA_REVISIONARE');

    // In modifica si precompila solo il testo: orari, ore e km non cambiano.
    useEffect(() => {
        if (!isEditingExisting || !reportGiornoCorrente) return;
        setRelazioneTesto(reportGiornoCorrente.testoRelazione || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportGiornoCorrente?.id, isEditingExisting]);

    // Incarico di un solo giorno: niente selettore data.
    const isSingleDay = useMemo(() => {
        const start = activeIncarico.dataInizio
            ? activeIncarico.dataInizio.split('T')[0]
            : null;
        const end = activeIncarico.dataFine
            ? activeIncarico.dataFine.split('T')[0]
            : null;
        // Stesso giorno o nessuna dataFine. Anche il continuativo ha un solo
        // report, pur coprendo due giorni.
        return start !== null && (end === null || start === end || !!activeIncarico.continuativo);
    }, [activeIncarico.dataInizio, activeIncarico.dataFine, activeIncarico.continuativo]);

    // Data del report e delle spese limitata a [dataInizio, dataFine];
    // senza dataFine vale il solo giorno di inizio.
    const dateLimits = useMemo(() => {
        const start = activeIncarico.dataInizio
            ? activeIncarico.dataInizio.split('T')[0]
            : undefined;
        // Continuativo: un solo report, datato al giorno di inizio.
        const end = activeIncarico.continuativo
            ? start
            : (activeIncarico.dataFine ? activeIncarico.dataFine.split('T')[0] : start);
        return { min: start, max: end };
    }, [activeIncarico.dataInizio, activeIncarico.dataFine, activeIncarico.continuativo]);

    const isDateOutsideIncarico = (d: string): boolean => {
        if (!dateLimits.min || !d) return false;
        if (d < dateLimits.min) return true;
        if (dateLimits.max && d > dateLimits.max) return true;
        return false;
    };

    const formatLimitsForToast = (): string => {
        if (!dateLimits.min) return '';
        const fmt = (iso: string) => {
            const [y, m, d] = iso.split('-');
            return `${d}/${m}/${y}`;
        };
        if (!dateLimits.max || dateLimits.min === dateLimits.max) return `il ${fmt(dateLimits.min)}`;
        return `dal ${fmt(dateLimits.min)} al ${fmt(dateLimits.max)}`;
    };

    // Il primo invio richiede almeno una sessione completa (start + stop);
    // nel re-invio si riscrive solo il testo e il vincolo non serve.
    const hasGpsSessions = attivitaGps.ids.length > 0;

    // Nel re-invio conta solo il testo. Gli orari indagine (ore cliente) sono obbligatori
    // al primo invio: il controllo è in handleSubmit, così il collaboratore vede il motivo.
    const isReportValid = useMemo(
        () => isEditingExisting
            ? relazioneTesto.trim().length > 10
            : (relazioneTesto.trim().length > 10 && hasGpsSessions),
        [relazioneTesto, isEditingExisting, hasGpsSessions]
    );

    const addSpesaRow = () =>
        setListaSpese(prev => [...prev, { dataAttivita: selectedDate, tipo: '', importo: '', note: '', anticipatoAzienda: false, file: null }]);

    const removeSpesaRow = (i: number) => setListaSpese(prev => prev.filter((_, idx) => idx !== i));

    const updateSpesa = (i: number, field: keyof SpesaRow, value: any) =>
        setListaSpese(prev => { const n = [...prev]; (n[i] as any)[field] = value; return n; });

    // Revocato: sola lettura, salvo un report DA_MODIFICARE di cui si può
    // ancora correggere il testo.
    // Lo storico non blocca il nuovo invio, per recuperare report dimenticati.
    const isRevocato = activeIncarico.stato === 'REVOCATO';
    const isLocked = !isEditingExisting && isRevocato;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!activeIncarico || !isReportValid || isLocked) return;

        // Re-invio: /modifica con il solo testo; il BE ignora i campi null,
        // quindi orari, km, spese e media restano invariati.
        if (isEditingExisting && reportGiornoCorrente?.id) {
            setIsSaving(true);
            try {
                await RelazioneGiornalieraService.modifica(reportGiornoCorrente.id, {
                    testoRelazione: relazioneTesto,
                });
                addToast('Report aggiornato e re-inviato al titolare', 'success');
                // Rettifica spontanea: si torna alla card "Report Già Trasmesso".
                setUserInitiatedEdit(false);
                onReportSent();
            } catch {
                addToast('Errore aggiornamento report', 'error');
            } finally {
                setIsSaving(false);
            }
            return;
        }

        // min/max dell'input non bastano (incolla, devtools): si ricontrolla
        // che la data sia nella finestra dell'incarico.
        if (isDateOutsideIncarico(selectedDate)) {
            addToast(`Data fuori dall'incarico: puoi compilare solo ${formatLimitsForToast()}.`, 'error');
            return;
        }
        if (!orarioInizioIndagine || !orarioFineIndagine) {
            addToast("Inserisci l'Orario Effettivo Servizio Investigativo (inizio e fine sul posto).", 'error');
            return;
        }

        for (let i = 0; i < listaSpese.length; i++) {
            const s = listaSpese[i];
            if (!s.dataAttivita) { addToast(`Spesa #${i + 1}: Inserisci la Data Spesa!`, 'error'); return; }
            if (isDateOutsideIncarico(s.dataAttivita)) {
                addToast(`Spesa #${i + 1}: data fuori dall'incarico (${formatLimitsForToast()}).`, 'error');
                return;
            }
            if (!s.tipo) { addToast(`Spesa #${i + 1}: Seleziona la Categoria!`, 'error'); return; }
            if (parseDecimal(s.importo) <= 0) { addToast(`Spesa #${i + 1}: Inserisci un Importo valido!`, 'error'); return; }
        }

        setIsSaving(true);
        try {
            const formData = new FormData();
            const payload = {
                incaricoId: activeIncarico.id,
                data: selectedDate,
                relazione: relazioneTesto,
                sessionIds: attivitaGps.ids,
                fileIds: mediaGiorno.tuttiIds,
                noteIds: mediaGiorno.noteIds,
                orarioInizioIndagine,
                orarioFineIndagine,
                oreGps: attivitaGps.ore,
                // Accetta virgola o punto, rifiuta i negativi.
                oreExtra: parseDecimal(oreExtra),
                km: parseDecimal(kmFatti),
                autoAziendale,
                spese: listaSpese.map(s => ({
                    dataAttivita: s.dataAttivita,
                    tipo: s.tipo,
                    importo: parseDecimal(s.importo),
                    note: s.note,
                    anticipatoAzienda: s.anticipatoAzienda,
                })),
            };
            formData.append('data', JSON.stringify(payload));
            listaSpese.forEach((spesa, idx) => { if (spesa.file) formData.append(`file_${idx}`, spesa.file); });

            await safeAction(
                { targetAction: 'REPORT_INVIATO', entityType: 'incarico', entityId: activeIncarico.id, payload: { km: parseDecimal(kmFatti), numSpese: listaSpese.length } },
                () => RelazioneGiornalieraService.create(formData),
            );
            addToast('Report e Spese Trasmessi con Successo', 'success');
            setRelazioneTesto(''); setListaSpese([]); setKmFatti(''); setOreExtra('');
            setAutoAziendale(false);
            setOrarioInizioIndagine(''); setOrarioFineIndagine('');
            onReportSent();
        } catch {
            addToast('Errore di trasmissione report', 'error');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2">
            {/* Header — data mostrata solo se l'incarico dura più di un giorno */}
            <div className="flex justify-between items-end pb-4 border-b border-slate-200">
                <h3 className="text-lg font-semibold text-slate-900">Report Fine Turno</h3>
                {!isSingleDay && (
                    <input
                        type="date"
                        className="bg-transparent border-b border-slate-300 text-sm font-semibold text-slate-700 outline-none pb-1"
                        value={selectedDate}
                        onChange={e => setSelectedDate(e.target.value)}
                        min={dateLimits.min}
                        max={dateLimits.max}
                        disabled={isLocked}
                    />
                )}
            </div>

            {/* Revocato: niente nuovi report, salvo il re-invio del testo di una
                relazione DA_MODIFICARE del giorno selezionato. */}
            {isRevocato && !isEditingExisting ? (
                <div className="bg-red-50 border-2 border-red-300 rounded-3xl p-8 text-center space-y-3 shadow-sm">
                    <h3 className="text-lg font-black text-red-800 uppercase tracking-wider">Incarico revocato dal titolare</h3>
                    <p className="text-sm text-red-700/90">
                        Non puoi più inviare o modificare report per questo incarico.
                    </p>
                </div>
            ) : reportGiornoCorrente && !isEditingExisting ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-3xl p-8 text-center space-y-4 shadow-sm animate-in zoom-in">
                    <CheckCircle size={48} className="text-emerald-500 mx-auto" />
                    <h3 className="text-xl font-bold text-emerald-900">Report Già Trasmesso</h3>
                    <p className="text-sm font-medium text-emerald-700">
                        Hai completato l'invio della relazione operativa
                        {!isSingleDay && ` per il ${new Date(selectedDate).toLocaleDateString('it-IT')}`}.
                    </p>
                    <span className="inline-block bg-white px-4 py-1.5 rounded-full text-[10px] font-bold text-emerald-800 uppercase tracking-widest border border-emerald-200 shadow-sm">
                        Stato: {reportGiornoCorrente.stato}
                    </span>
                    {canCollaboratorEdit && !isRevocato ? (
                        <div className="pt-2 space-y-2">
                            <button
                                type="button"
                                onClick={() => setUserInitiatedEdit(true)}
                                className="inline-flex items-center gap-2 bg-white hover:bg-emerald-50 border-2 border-emerald-300 text-emerald-800 font-bold text-sm px-5 py-2.5 rounded-xl shadow-sm transition-colors"
                            >
                                <RotateCcw size={16} /> Modifica relazione
                            </button>
                            <p className="text-[11px] text-emerald-600/80 leading-snug max-w-md mx-auto">
                                Puoi correggere il testo della relazione anche dopo l'invio. Al salvataggio il report tornerà in attesa di revisione del titolare.
                            </p>
                        </div>
                    ) : (
                        <p className="text-xs text-emerald-600 mt-4 opacity-80">
                            Le modifiche successive devono essere richieste all'amministrazione.
                        </p>
                    )}
                </div>
            ) : (
                <form onSubmit={handleSubmit} className="space-y-8">
                    {/* Il titolare ha chiesto modifiche: mostra il motivo e riapre il form. */}
                    {isEditingExisting && reportGiornoCorrente?.stato === 'DA_MODIFICARE' && (
                        <div className="bg-amber-50 border-2 border-amber-300 text-amber-900 p-4 rounded-2xl flex gap-3 text-sm font-medium shadow-sm">
                            <AlertTriangle size={26} className="shrink-0 text-amber-600 mt-0.5" />
                            <div className="min-w-0">
                                <p className="font-black uppercase tracking-wider text-xs mb-1.5 flex items-center gap-2">
                                    Richiesta modifiche dal titolare
                                    <span className="inline-block bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full text-[9px] uppercase font-black tracking-wider">Da Modificare</span>
                                </p>
                                {reportGiornoCorrente?.motivoRichiestaModifica ? (
                                    <p className="leading-relaxed whitespace-pre-wrap text-amber-900/90">
                                        "{reportGiornoCorrente.motivoRichiestaModifica}"
                                    </p>
                                ) : (
                                    <p className="leading-relaxed text-amber-900/80 italic">
                                        Nessun motivo specifico fornito.
                                    </p>
                                )}
                                <p className="text-[11px] text-amber-700/80 mt-2">
                                    Modifica i campi qui sotto e re-invia. Le spese e i media già allegati restano invariati.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Rettifica avviata dal collaboratore, con "Annulla" per uscire senza salvare. */}
                    {userInitiatedEdit && reportGiornoCorrente?.stato !== 'DA_MODIFICARE' && (
                        <div className="bg-blue-50 border-2 border-blue-300 text-blue-900 p-4 rounded-2xl flex gap-3 text-sm font-medium shadow-sm">
                            <RotateCcw size={26} className="shrink-0 text-blue-600 mt-0.5" />
                            <div className="min-w-0 flex-1">
                                <p className="font-black uppercase tracking-wider text-xs mb-1.5">
                                    Stai modificando una relazione già trasmessa
                                </p>
                                <p className="leading-relaxed text-blue-900/85">
                                    Riscrivi il testo qui sotto e clicca "Trasmetti" per re-inviare. Il titolare la riceverà di nuovo per la revisione. Le spese e i media restano invariati.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setUserInitiatedEdit(false);
                                        setRelazioneTesto('');
                                    }}
                                    className="mt-2 text-[11px] font-bold text-blue-700 hover:text-blue-900 underline underline-offset-2"
                                >
                                    Annulla modifica
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Banner info — solo in modalità "nuovo report", non quando si sta ri-editando */}
                    {!isEditingExisting && (
                    <div className="bg-blue-50 text-blue-800 p-4 rounded-xl flex gap-3 text-sm font-medium border border-blue-200">
                        <Info size={24} className="shrink-0 text-blue-500 mt-0.5" />
                        <p>I file multimediali scattati oggi o assegnati a questa data <strong>verranno catalogati e aggiunti automaticamente</strong> alla relazione in amministrazione.</p>
                    </div>
                    )}

                    {/* Testo relazione */}
                    <div className={`bg-white border rounded-2xl shadow-sm focus-within:ring-2 focus-within:ring-blue-500 transition-all ${relazioneTesto.trim().length < 10 ? 'border-amber-400 ring-1 ring-amber-200' : 'border-slate-200'} ${isLocked ? 'opacity-60 grayscale pointer-events-none' : ''}`}>
                        <div className="bg-slate-50 border-b border-slate-200 px-4 py-2.5 rounded-t-2xl flex justify-between items-center">
                            <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Testo Relazione Ufficiale</label>
                            {relazioneTesto.trim().length < 10 && !isLocked && (
                                <span className="text-[10px] font-bold text-amber-600">Richiesto (Min. 10 caratt.)</span>
                            )}
                        </div>
                        <textarea
                            className="w-full p-4 bg-transparent border-0 text-sm font-medium text-slate-700 placeholder-slate-400 outline-none min-h-[120px] resize-y"
                            placeholder="Descrivi dettagliatamente gli spostamenti e le azioni rilevanti per l'ufficio..."
                            value={relazioneTesto}
                            onChange={e => setRelazioneTesto(e.target.value)}
                            disabled={isLocked}
                        />
                    </div>

                    {/* "Gestione Tempi" nascosta nel re-invio: orari, ore e km non cambiano. */}
                    {!isEditingExisting && (
                    <div className={isLocked ? 'opacity-60 grayscale pointer-events-none' : ''}>
                        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 px-1">Gestione Tempi</h3>

                        {/* Orari indagine obbligatori: sono le ore cliente da fatturare. */}
                        <div className="bg-blue-50 border border-blue-200 p-4 sm:p-5 rounded-2xl mb-4 shadow-sm">
                            <label className="text-[10px] font-bold text-blue-800 uppercase tracking-wide block mb-3 flex items-center gap-1.5">
                                <Clock size={14} className="text-blue-600" /> Orario Effettivo Servizio Investigativo <span className="text-red-500">*</span>
                            </label>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide block mb-1">Inizio Sul Posto</label>
                                    <input type="time" required className="w-full bg-white border border-slate-200 p-2.5 rounded-lg font-semibold text-slate-800 outline-none focus:ring-2 ring-blue-500" value={orarioInizioIndagine} onChange={e => setOrarioInizioIndagine(e.target.value)} disabled={isLocked} />
                                </div>
                                <div>
                                    <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wide block mb-1">Fine Sul Posto</label>
                                    <input type="time" required className="w-full bg-white border border-slate-200 p-2.5 rounded-lg font-semibold text-slate-800 outline-none focus:ring-2 ring-blue-500" value={orarioFineIndagine} onChange={e => setOrarioFineIndagine(e.target.value)} disabled={isLocked} />
                                </div>
                            </div>
                            <p className="text-[9px] text-blue-600/80 mt-2 font-medium leading-tight">
                                Obbligatorio: sono le ore cliente, base delle ore da fatturare.
                            </p>
                        </div>

                        {/* Ore GPS + Extra + Km */}
                        <div className="grid grid-cols-3 gap-3">
                            <div className="bg-emerald-50 border border-emerald-100 p-4 rounded-2xl flex flex-col justify-center shadow-sm">
                                <label className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-wide mb-1 flex items-center gap-1">
                                    <Activity size={12} /> Ore Registrate
                                </label>
                                <div className="text-2xl font-black text-emerald-700">
                                    {attivitaGps.ore}<span className="text-sm font-semibold text-emerald-600/50">h</span>
                                </div>
                                <span className="text-[8px] text-emerald-600 font-medium uppercase mt-1">Calcolate in automatico</span>
                            </div>
                            <div className="bg-white border border-slate-200 p-3 rounded-2xl shadow-sm focus-within:ring-2 ring-blue-100">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Ore Extra (Ufficio)</label>
                                <NumberInput
                                       min={0} step="0.5" className="w-full bg-transparent font-semibold text-lg text-slate-800 outline-none" value={oreExtra} onChange={e => setOreExtra(e.target.value)} placeholder="0.0" disabled={isLocked} />
                            </div>
                            <div className="bg-white border border-slate-200 p-3 rounded-2xl shadow-sm focus-within:ring-2 ring-blue-100">
                                <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wide block mb-1">Km Percorsi</label>
                                <NumberInput
                                       min={0} className="w-full bg-transparent font-semibold text-lg text-slate-800 outline-none" value={kmFatti} onChange={e => setKmFatti(e.target.value)} placeholder="0" disabled={isLocked} />
                                {/* Auto aziendale / privata — mostrato solo se ha inserito km */}
                                {parseDecimal(kmFatti) > 0 && (
                                    <AutoAziendaleSelector className="mt-2" value={autoAziendale} onChange={setAutoAziendale} disabled={isLocked} />
                                )}
                            </div>
                        </div>
                    </div>
                    )}

                    {/* Spese non modificabili nel re-invio */}
                    {!isEditingExisting && (
                    <div className={`pt-2 border-t border-slate-200 mt-6 ${isLocked ? 'opacity-60 grayscale pointer-events-none' : ''}`}>
                        <div className="flex items-center justify-between mt-6 mb-3 px-1">
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                                <Receipt size={14} /> Spese e Scontrini
                            </h3>
                            <button type="button" onClick={addSpesaRow} disabled={isLocked} className="text-xs font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg flex items-center gap-1 hover:bg-blue-100 transition-colors disabled:opacity-50">
                                <Plus size={14} /> Aggiungi
                            </button>
                        </div>

                        <div className="space-y-4">
                            {listaSpese.length === 0 && (
                                <div className="text-center py-6 border border-dashed border-slate-200 rounded-xl text-slate-400 text-sm">Nessuna spesa extra da rendicontare.</div>
                            )}
                            {listaSpese.map((s, i) => (
                                <div key={i} className="flex flex-col gap-4 p-5 bg-white border border-slate-200 rounded-xl shadow-sm animate-in fade-in relative group">
                                    <button type="button" onClick={() => removeSpesaRow(i)} className="absolute -top-2 -right-2 bg-white border border-slate-200 text-slate-400 hover:text-rose-500 rounded-full p-1.5 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity">
                                        <X size={16} />
                                    </button>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        {/* Data spesa: nascosta se giorno singolo (usa selectedDate automaticamente) */}
                                        {!isSingleDay && (
                                            <div>
                                                <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Data Spesa *</label>
                                                <input type="date" required min={dateLimits.min} max={dateLimits.max} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold focus:ring-2 ring-blue-500 outline-none" value={s.dataAttivita} onChange={e => updateSpesa(i, 'dataAttivita', e.target.value)} />
                                            </div>
                                        )}
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Categoria *</label>
                                            <select required className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm font-semibold focus:ring-2 ring-blue-500 outline-none" value={s.tipo} onChange={e => updateSpesa(i, 'tipo', e.target.value)}>
                                                <option value="">-- Seleziona --</option>
                                                <option value="Pasti">Pasti</option>
                                                <option value="Trasporti">Trasporti</option>
                                                <option value="Carburante">Carburante</option>
                                                <option value="Alloggio">Alloggio</option>
                                                <option value="Altro">Altro</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Importo (€) *</label>
                                            <div className="relative">
                                                <span className="absolute left-3 top-2.5 text-slate-400 font-bold">€</span>
                                                <NumberInput
                                                       min={0} step="0.01" required className="w-full p-2.5 pl-8 bg-slate-50 border border-slate-200 rounded-lg text-sm font-bold focus:ring-2 ring-blue-500 outline-none" value={s.importo} onChange={e => updateSpesa(i, 'importo', e.target.value)} placeholder="0.00" />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex flex-col md:flex-row gap-4 items-start">
                                        <div className="flex-1 w-full">
                                            <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">Note Brevi</label>
                                            <input type="text" placeholder="Es. Pranzo, Parcheggio, ecc..." className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-lg text-sm focus:ring-2 ring-blue-500 outline-none" value={s.note} onChange={e => updateSpesa(i, 'note', e.target.value)} />
                                        </div>
                                        <div className="w-full md:w-64 bg-purple-50 border border-purple-200 p-2.5 rounded-lg flex items-center gap-3 mt-0 md:mt-5 shrink-0">
                                            <input type="checkbox" id={`carta-${i}`} className="w-4 h-4 text-purple-600 rounded cursor-pointer" checked={s.anticipatoAzienda} onChange={e => updateSpesa(i, 'anticipatoAzienda', e.target.checked)} />
                                            <label htmlFor={`carta-${i}`} className="text-[10px] font-bold text-purple-900 cursor-pointer leading-tight">
                                                CARTA AZIENDALE <br /><span className="font-normal opacity-80">(Nessun rimborso)</span>
                                            </label>
                                        </div>
                                        <label className={`w-full md:w-32 flex items-center justify-center p-3 border rounded-lg transition-all cursor-pointer mt-0 md:mt-5 ${s.file ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'}`}>
                                            <Camera size={16} className="mr-1.5" />
                                            <span className="text-[10px] font-bold uppercase tracking-wider">{s.file ? 'FOTO OK' : 'SCONTRINO'}</span>
                                            <input type="file" capture="environment" accept="image/*" className="hidden" onChange={e => { if (e.target.files) updateSpesa(i, 'file', e.target.files[0]); }} />
                                        </label>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                    )}

                    {/* Nessuna sessione GPS: spiega perché l'invio è disabilitato */}
                    {!isEditingExisting && !hasGpsSessions && !isLocked && (
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-800 text-xs font-medium">
                            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                            <div>
                                <p className="font-bold mb-0.5">Nessuna sessione registrata</p>
                                <p className="text-amber-700/90 leading-relaxed">
                                    Per inviare il report definitivo devi prima fare almeno uno Start e uno Stop nell'incarico.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Bottone invio: testo e icona cambiano nel re-invio */}
                    <div className="pt-2">
                        <button
                            type="submit"
                            disabled={isSaving || !isReportValid || isLocked}
                            className={`w-full text-white py-4 rounded-xl font-semibold text-lg shadow-lg transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${isEditingExisting ? 'bg-amber-600 hover:bg-amber-700' : 'bg-slate-900 hover:bg-slate-800'}`}
                        >
                            {isSaving ? <Loader2 className="animate-spin" size={20} /> : (isEditingExisting ? <RotateCcw size={20} /> : <Send size={20} />)}
                            {isLocked
                                ? 'Non Modificabile'
                                : isSaving
                                    ? (isEditingExisting ? 'Re-invio in corso...' : 'Trasmissione in corso...')
                                    : (isEditingExisting ? 'Re-invia Report al Titolare' : 'Invia Report Definitivo')}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
};